import { useEffect, useRef, useState } from "react"
import { useLocation, useParams } from "react-router-dom"
import { 
  Dict,
  Imu,
  IsolatedScanIMU,
  IsolatedScanMotion,
  IsolatedScanThermal,
  IsolatedScanTHzMetadata,
  Motion,
  ProcessedHeatmapImage,
  ProcessedSection,
  ScanMedia,
  StarAPIFetchState,
  Thermal,
} from "../types/types"
import { Workflow, SectionScan, IsolatedScan, AngularSectionScan, PatchSectionScan } from "../types/starAPIResponseTypes"
import { getIsolatedScan } from "../service/isolatedScanService"
import { getSectionScans } from "../service/sectionScanService"
import { getIsolatedScanImu } from "../service/isolatedScanImuService"
import { getIsolatedScanThermal } from "../service/isolatedScanThermalService"
import { getIsolatedScanMotion } from "../service/isolatedScanMotionService"
import { getScanMediaForSection } from "../service/scanMediaService"
import { getProcessedHeatmapImageMask, getProcessedIRHeatmapImage, getProcessedMetalHeatmapImage, getProcessedSectionData, getProcessedTHzHeatmapImage } from "../service/processedImages"
import { getWorkflow } from "../service/workflowService"
import { STARAPIError } from "../service/service"
import WorkflowType from "../enums/workflowType"
import PatchCornerType from "../enums/patchCornerTypes"

const useLoadProcessedWorkflow = () =>{
  const { id: workflowId } = useParams<{ id: string }>()

  const [workflow, setWorkflow] = useState<StarAPIFetchState<Workflow>>({ status: "idle" })
  const [sections, setSections] = useState<StarAPIFetchState<SectionScan[]>>({ status: "idle" })
  const [isolatedScans, setIsolatedScans] = useState<StarAPIFetchState<IsolatedScan[]>[]>([])
  const [images, setImages] = useState<StarAPIFetchState<ScanMedia[]>[]>([])
  const [processedSection, setProcessedSection] = useState<StarAPIFetchState<ProcessedSection>[]>([])
  const [heatmapMask, setHeatmapMask] = useState<StarAPIFetchState<Uint8Array[]>[]>([])
  const [heatmapTHz, setHeatmapThz] = useState<StarAPIFetchState<Uint8Array[][]>[]>([])
  const [heatmapIR, setHeatmapIR] = useState<StarAPIFetchState<Uint8Array[][]>[]>([])
  const [heatmapMetal, setHeatmapMetal] = useState<StarAPIFetchState<Uint8Array[][]>[]>([])

  const isLoadedRef = useRef(false)

  useEffect(() => {
    if (isLoadedRef.current) { return }
    const loadWorkflowData = async () => {
      isLoadedRef.current = true
      
      if (workflowId === undefined) {
        setWorkflow({ status: "error", error: new Error("No workflow ID provided") })
        return
      }

      const workflow = await getWorkflow(parseInt(workflowId))
      if (workflow instanceof Error) {
        setWorkflow({ status: "error", error: new Error("Failed to fetch workflow") })
        return
      }
      setWorkflow({ status: "success", data: workflow })
      const invertHeatmapScans = workflow.workflow_type === WorkflowType.PATCH      

      const sectionResponse = await getSectionScans(parseInt(workflowId))
      if (sectionResponse instanceof Error) { 
        setSections({ status: "error", error: sectionResponse })
        return
      }

      const sectionScans: SectionScan[] = sectionResponse
      let invertFirstIsolatedScans = false
      
      if (sectionScans.length === 0) {
        setSections({ status: "error", error: new Error("No sections found") })
        return
      }
      
      setSections({ status: "success", data: sectionScans })

      setSectionHeatmapsLoading(sectionScans.length)


      // Load first section thz heatmap and metadata with priority 1.
      const firstSectionId = sectionScans[0].section_scan_id
      if (sectionScans[0].section_type === WorkflowType.PATCH) {
        const patchSection = sectionScans as PatchSectionScan[]
        invertFirstIsolatedScans = patchSection[0].scan_corner_type === 1
      }
      await Promise.all([
        setHeatmapImageMaskForSection(firstSectionId, 0, invertHeatmapScans),
        setProcessedSectionData(firstSectionId, 0, sectionScans[0].is_skipped, invertHeatmapScans),
        setTHzHeatmapsForSection(firstSectionId, 0, false),
        setIRHeatmapsForSection(firstSectionId, 0, false),
        setMetalHeatmapsForSection(firstSectionId, 0, false),
        setIsolatedScanForSection(firstSectionId, 0, invertFirstIsolatedScans)
      ])
      
      // Load remaining sections thz heatmaps and metadata with priority 2.
      if (sectionScans.length > 1) {        
        const remainingSections = sectionScans.slice(1)
        for (let i=0; i<remainingSections.length; i++) {
          const section = remainingSections[i]
          let invertIsolatedScans = false
          if (section.section_type === WorkflowType.PATCH) {
            const patchSection = section as PatchSectionScan
            invertIsolatedScans = patchSection.scan_corner_type === 1
          }
          await Promise.all([
            setHeatmapImageMaskForSection(section.section_scan_id, section.section_no, invertHeatmapScans),
            setProcessedSectionData(section.section_scan_id, section.section_no, section.is_skipped, invertHeatmapScans),
            setTHzHeatmapsForSection(section.section_scan_id, section.section_no, false),
            setIRHeatmapsForSection(section.section_scan_id, section.section_no, false),
            setMetalHeatmapsForSection(section.section_scan_id, section.section_no, false),
            setIsolatedScanForSection(section.section_scan_id, section.section_no, invertIsolatedScans),
          ])
        }
      }

      // Load images with priority 4.
      for (let i=0; i<sectionScans.length; i++) {
        const section = sectionScans[i]
        await setImagesForSection(section.section_scan_id, section.section_no)
      }   
    }
    loadWorkflowData()
  }, [workflowId])

  /**
   * Sets loading state for section heatmaps to show loading skeletons in the UI while fetching data.
   * 
   * @param numberOfSections to pre load
   */
  const setSectionHeatmapsLoading = (numberOfSections: number) => {
    const sectionHeatmaps: StarAPIFetchState<Uint8Array[][]>[] = []
    const processedSectionsData: StarAPIFetchState<ProcessedSection>[] = []
    const isolatedScan: StarAPIFetchState<IsolatedScan[]>[] = []
    const mask: StarAPIFetchState<Uint8Array[]>[] = []
    const thermal: StarAPIFetchState<Thermal[]>[] = []
    const image: StarAPIFetchState<ScanMedia[]>[] = []

    for (let i=0; i<numberOfSections; i++) {
      sectionHeatmaps.push({ status: "loading" })
      processedSectionsData.push({ status: "loading" })
      isolatedScan.push({ status: "loading" })
      thermal.push({ status: "loading" })
      image.push({ status: "loading" })
      mask.push({ status: "loading" })
    }
    setProcessedSection(processedSectionsData)
    setHeatmapThz(sectionHeatmaps)
    setHeatmapIR(sectionHeatmaps)
    setHeatmapMetal(sectionHeatmaps)
    setHeatmapMask(mask)
    setIsolatedScans(isolatedScan)
    setImages(image)
  }

  /**
   * Sets the processed section data for a section.
   * 
   * @param sectionId section id to set processed section data for
   * @param sectionNumber index of section
   */
  const setProcessedSectionData = async (sectionId: number, sectionNumber: number, isSkipped: boolean, hideFeatureCoords: boolean) => {    
    setProcessedSection(prevState =>
      updateSectionData<StarAPIFetchState<ProcessedSection>>(prevState, { status: "loading" }, sectionNumber)
    )

    const response = await getProcessedSectionData(sectionId)
    if (response instanceof Error) {
      const error = response as STARAPIError

      if (error.statusCode === 404 && isSkipped) {
        setProcessedSection(prevState => 
          updateSectionData<StarAPIFetchState<ProcessedSection>>(prevState, { status: "error", error: new Error("No heatmap available. The selected section was skipped.") }, sectionNumber)
        )
        return
      }

      if (error.statusCode === 404) {
        setProcessedSection(prevState => 
          updateSectionData<StarAPIFetchState<ProcessedSection>>(prevState, { status: "error", error: new Error("Heatmap not available. This section may still be processing. If the issue continous please contact Subtera support.") }, sectionNumber)
        )
        return
      }
      setProcessedSection(prevState => 
          updateSectionData<StarAPIFetchState<ProcessedSection>>(prevState, { status: "error", error: error }, sectionNumber)
        )
        return
    }
    const processedSection: ProcessedSection = {
      id: response.id,
      section_scan_id: response.section_scan_id,
      processing_start_time: response.processing_start_time,
      processing_end_time: response.processing_end_time,
      processing_version: response.processing_version,
      feature_vectors: hideFeatureCoords ? [] : JSON.parse(response.feature_vectors),
      feature_coords: JSON.parse(response.feature_coords),
      feature_vectors_inverted: JSON.parse(response.feature_vectors_inverted),
      feature_coords_inverted: JSON.parse(response.feature_coords_inverted),
      thz_min_range_value: response.thz_min_range_value,
      thz_max_range_value: response.thz_max_range_value,
      ir_min_range_value: response.ir_min_range_value,
      ir_max_range_value: response.ir_max_range_value,
      metal_min_range_value: 0,
      metal_max_range_value: 1,
      background_value: response.background_value,
      heatmaps_image_blob_id: response.heatmaps_image_blob_id,
      heatmap_blob_file_version: response.heatmap_file
    }
    
    setProcessedSection(prevState =>
      updateSectionData<StarAPIFetchState<ProcessedSection>>(prevState, { status: "success", data: processedSection }, sectionNumber)
    )
  }

  /**
   * Sets all isolated scans isolated scan for a given section.
   * 
   * @param sectionId to fetch isolated scans for
   * 
   * @return isolated scan for the given section
   */
  const setIsolatedScanForSection = async (sectionId: number, sectionNumber: number, invertScans: boolean) => {
    setIsolatedScans(prevState =>
      updateSectionData<StarAPIFetchState<IsolatedScan[]>>(prevState, { status: "loading" }, sectionNumber)
    )
    const response = await getIsolatedScan(sectionId)
    if (response instanceof Error) {
      setIsolatedScans(prevState =>
        updateSectionData<StarAPIFetchState<IsolatedScan[]>>(prevState, { status: "error", error: response }, sectionNumber)
      )
      return
    }
    const isolatedScan = response as IsolatedScan[]

    setIsolatedScans(prevState =>
      updateSectionData<StarAPIFetchState<IsolatedScan[]>>(
        prevState,
        { status: "success", data: invertScans ? isolatedScan.reverse() : isolatedScan },
        sectionNumber
      )
    )
  }

  /**
   * Sets the THz heatmaps for a section.
   * 
   * @param sectionId of processed section to fetch
   */
  const setTHzHeatmapsForSection = async (sectionId: number, sectionNumber: number, invertScans: boolean) => {
    setHeatmapThz(prevState =>
      updateHeatmapForIndex(prevState, { status: "loading" }, sectionNumber)
    )
    
    const response = await getProcessedTHzHeatmapImage(sectionId)
    if (response instanceof Error) {
      setHeatmapThz(prevState =>
        updateHeatmapForIndex(prevState, { status: "error", error: response }, sectionNumber)
      )
      return
    }
    const sectionHeatmap = await extractSectionHeatmap(response, 4, invertScans)

    setHeatmapThz(prevState =>
      updateHeatmapForIndex(prevState, { status: "success", data: sectionHeatmap }, sectionNumber)
    )
  }

  /**
   * Sets the infrared heatmaps for a section.
   * 
   * @param sectionId of processed section to fetch
   */
  const setIRHeatmapsForSection = async (sectionId: number, sectionNumber: number, invertScans: boolean) => {
    setHeatmapIR(prevState => 
      updateHeatmapForIndex(prevState, { status: "loading" }, sectionNumber)
    )
    
    const response = await getProcessedIRHeatmapImage(sectionId)       
    if (response instanceof Error) {
      setHeatmapIR(prevState => 
        updateHeatmapForIndex(prevState, { status: "error", error: response }, sectionNumber)
      )
      return
    }
    const sectionHeatmap = await extractSectionHeatmap(response, 2, invertScans)

    setHeatmapIR(prevState =>
      updateHeatmapForIndex(prevState, { status: "success", data: sectionHeatmap }, sectionNumber)
    )
  }

  /**
   * Sets the metal heatmaps for a section.
   * 
   * @param sectionId of processed section to fetch
   */
  const setMetalHeatmapsForSection = async (sectionId: number, sectionNumber: number, invertScans: boolean) => {
    setHeatmapMetal(prevState => 
      updateHeatmapForIndex(prevState, { status: "loading" }, sectionNumber)
    )
    
    const response = await getProcessedMetalHeatmapImage(sectionId)       
    if (response instanceof Error) {
      setHeatmapMetal(prevState => 
        updateHeatmapForIndex(prevState, { status: "error", error: response }, sectionNumber)
      )
      return
    }
    const sectionHeatmap = await extractSectionHeatmap(response, 1, invertScans)
        
    setHeatmapMetal(prevState =>
      updateHeatmapForIndex(prevState, { status: "success", data: sectionHeatmap }, sectionNumber)
    )
  }

  /**
   * Sets the metal heatmaps for a section.
   * 
   * @param sectionId of processed section to fetch
   */
  const setHeatmapImageMaskForSection = async (sectionId: number, sectionNumber: number, invertScans: boolean) => {
    setHeatmapMask(prevState => 
      updateSectionData<StarAPIFetchState<Uint8Array[]>>(prevState, { status: "loading" }, sectionNumber)
    )
    
    const response = await getProcessedHeatmapImageMask(sectionId)
    if (response instanceof Error) {
      const error = response as STARAPIError

      if (error.statusCode === 404) {
        setHeatmapMask(prevState =>
          updateSectionData<StarAPIFetchState<Uint8Array[]>>(prevState, { status: "success", data: null }, sectionNumber)
        )
        return
      } 
      setHeatmapMask(prevState => 
        updateSectionData<StarAPIFetchState<Uint8Array[]>>(prevState, { status: "error", error: response }, sectionNumber)
      )
      return
    }

    const height = Number(response.headers.get("X-Heatmap-Height"))
    const width = Number(response.headers.get("X-Heatmap-Width"))
    
    const buffer = await response.arrayBuffer()
    const flat = new Uint8Array(buffer)

    const mask = Array.from({ length: height }, (_, y) =>
      flat.subarray(y * width, y * width + width)
    )
    
    setHeatmapMask(prevState =>
      updateSectionData<StarAPIFetchState<Uint8Array[]>>(
        prevState, 
        { status: "success", data: invertScans ? mask.reverse() : mask },
        sectionNumber
      )
    )
  }

  /**
   * Sets images for the given section.
   * 
   * @param sectionId to fetch images for
   * 
   * @returns images for the section
   */
  const setImagesForSection = async (sectionId: number, sectionNumber: number) => {
    setImages(prevState =>
      updateSectionData<StarAPIFetchState<ScanMedia[]>>(prevState, { status: "loading" }, sectionNumber)
    )
    const response = await getScanMediaForSection(sectionId)
    if (response instanceof Error) {
      setImages(prevState =>
      updateSectionData<StarAPIFetchState<ScanMedia[]>>(prevState, { status: "error", error: response }, sectionNumber)
    )
      return
    }

    const images = response as ScanMedia[]
    setImages(prevState =>
      updateSectionData<StarAPIFetchState<ScanMedia[]>>(prevState, { status: "success", data: images }, sectionNumber)
    )
  }

  /**
   * Gets section heatmap from STAR API heatmap response.
   * 
   * @param response to extract section heatmap from
   * @param numberOfChannels per scan of heatmap
   * 
   * @returns section heatmap
   */
  const extractSectionHeatmap = async (response: Response, numberOfChannels: number, invertScans: boolean) => {
    const height = Number(response.headers.get("X-Heatmap-Height"))
    const width = Number(response.headers.get("X-Heatmap-Width"))
    
    const buffer = await response.arrayBuffer()
    const flat = new Uint8Array(buffer)
    const numScans = height / numberOfChannels

    const sectionHeatmap: Uint8Array[][] = Array.from({ length: numScans }, (_, scan) =>
      Array.from({ length: numberOfChannels }, (_, channel) => {
        const rowIndex = scan * numberOfChannels + channel
        return flat.subarray(rowIndex * width, rowIndex * width + width)
      })
    )
    if (invertScans) {
      return sectionHeatmap.reverse()
    }
    return sectionHeatmap
  }

  /**
   * Gets copy of a given heatmap with an updated value for a given index.
   * 
   * @param prevState to copy and update
   * @param state to update
   * @param index of the state to update
   * 
   * @returns copy of updated state
   */
  const updateHeatmapForIndex = (prevState: StarAPIFetchState<Uint8Array[][]>[], state: StarAPIFetchState<Uint8Array[][]>, index: number) => {
    return prevState.map((heatmap, i) => 
      i === index ? state : heatmap
    )
  }

  const updateSectionData = <T,>(prevState: T[], state: T, index: number) => {
    return prevState.map((section, i) => 
      i === index ? state : section
    )
  }
  
  return [{
    workflow,
    sections,
    processedSection,
    heatmapTHz,
    heatmapIR,
    heatmapMetal,
    heatmapMask,
    isolatedScans,
    images
  }]
}

export default useLoadProcessedWorkflow